/**
 * useEntityUpdate - Centralized single-entity update hook for ORGANIZATIONS_TABLE entities
 *
 * This hook provides a unified way to update individual organizations, deals, and products
 * with consistent cache management across all data stores.
 *
 * Features:
 * - Single field or multi-field updates
 * - Full entity updates (replaces useUpdateOrganization, useUpdateDeal, etc.)
 * - Optimistic UI updates via apiRef (instant grid feedback)
 * - Consistent cache updates (SEARCH_DATA_KEY + server-side search queries)
 * - Automatic rollback on error
 * - Type-safe entity detection
 *
 * Usage:
 * ```tsx
 * const { updateEntity, updateEntityField, updateOrganization, updateDeal } = useEntityUpdate({ apiRef })
 *
 * // Update multiple fields at once (field-level API)
 * await updateEntity('organization', { PK, SK, org_uuid }, { status: 'Active', notes: 'Updated' })
 *
 * // Update a single field
 * await updateEntityField('organization', { PK, SK }, 'status', 'Active')
 *
 * // Full entity update (legacy API - replaces useUpdateOrganization)
 * updateOrganization({ orgId: org.org_uuid, orgData: { ...updatedFields } })
 *
 * // Full deal update (legacy API - replaces useUpdateDeal)
 * updateDeal({ PK, SK, dealData: { ...updatedFields } })
 * ```
 */

import { useCallback, useRef } from 'react'
import { useQueryClient, QueryKey, useMutation } from '@tanstack/react-query'
import { GridApiPremium } from '@mui/x-data-grid-premium'
import { MutableRefObject } from 'react'
import { OrganizationTableItem, Organization, NewDeal } from 'types'
import { useUpdateFields } from './organizations_table/useUpdateFields'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { SEARCH_DATA_KEY } from 'api/search'
import { normalizeTeam } from 'utils/normalizeTeam'
import { baseUrl } from 'api/config'
import axios from 'axios'

// Supported entity types
type EntityType = 'organization' | 'deal' | 'product'

// Entity identifier - flexible to support various ID formats
type EntityId = {
  PK: string
  SK: string
  org_uuid?: string
  deal_uuid?: string
  instanceId?: string
}

interface UseEntityUpdateOptions {
  /** Pass apiRef for instant grid row updates */
  apiRef?: MutableRefObject<GridApiPremium>
  /** Show success/error snackbars (default: true) */
  showNotifications?: boolean
}

// Legacy mutation variable types (for backward compatibility)
type UpdateOrgVariables = {
  orgId: string
  orgData: Partial<Organization>
}

type UpdateOrgItemVariables = {
  PK: string
  SK: string
  orgData: Partial<Organization>
}

type UpdateDealVariables = {
  PK: string
  SK: string
  dealData: Partial<NewDeal> | Record<string, any>
  dealId?: string
}

// Fields that should always be stored as arrays
const ARRAY_FIELDS = [
  'team_details',
  'affiliated',
  'contractedBy',
  'primaryContact',
  'billingContact',
  'contacts',
  'owner',
  'onboardingTeam',
]

// Configuration for each entity type
const ENTITY_CONFIG = {
  organization: {
    dataKey: 'organizations' as const,
    tableName: 'ORGANIZATIONS_TABLE',
    idField: 'org_uuid',
    searchKeys: ['organizations', 'mtb'],
  },
  deal: {
    dataKey: 'deals' as const,
    tableName: 'ORGANIZATIONS_TABLE',
    idField: 'deal_uuid',
    searchKeys: ['deals', 'nco'],
  },
  product: {
    dataKey: 'products' as const,
    tableName: 'ORGANIZATIONS_TABLE',
    idField: 'instanceId',
    searchKeys: ['products'],
  },
}

/**
 * Normalize value for storage - handle array fields consistently
 */
const normalizeValue = (field: string, value: any, usersIndex?: Map<string, any>): any => {
  // Special handling for team_details with user expansion
  if (field === 'team_details' && usersIndex) {
    return normalizeTeam(Array.isArray(value) ? value : [], usersIndex)
  }

  if (ARRAY_FIELDS.includes(field)) {
    return Array.isArray(value) ? value : value !== undefined ? [value] : []
  }

  // Flatten single-item arrays for non-array fields
  if (Array.isArray(value) && value.length === 1) {
    return value[0]
  }

  return value
}

/**
 * Get the row ID that matches the grid's getRowId function
 */
const getGridRowId = (entity: EntityId, entityType: EntityType): string => {
  const config = ENTITY_CONFIG[entityType]
  // Most grids use rowId which is typically the uuid or composite key
  return entity[config.idField] || `${entity.PK}::${entity.SK}`
}

export const useEntityUpdate = (options: UseEntityUpdateOptions = {}) => {
  const { apiRef, showNotifications = true } = options

  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const { mutateAsync: updateFields } = useUpdateFields()

  // Store previous state for rollback
  const rollbackRef = useRef<{ key: QueryKey; data: any }[]>([])

  /**
   * Update grid row directly via apiRef for instant visual feedback
   */
  const updateGridRow = useCallback(
    (rowId: string, updates: Record<string, any>) => {
      if (!apiRef?.current?.updateRows) return

      try {
        apiRef.current.updateRows([{ id: rowId, rowId, ...updates }])
      } catch (error) {
        console.error('[useEntityUpdate] Grid update failed:', error)
      }
    },
    [apiRef]
  )

  /**
   * Update all relevant caches with the new field values
   */
  const updateCaches = useCallback(
    (
      entityType: EntityType,
      entityId: Partial<EntityId>,
      updates: Record<string, any>,
      saveForRollback = true
    ) => {
      const config = ENTITY_CONFIG[entityType]

      // Helper to check if a row matches our entity
      const isMatch = (row: any): boolean => {
        if (entityId.PK && entityId.SK && row.PK === entityId.PK && row.SK === entityId.SK) return true
        if (config.idField && entityId[config.idField] && row[config.idField] === entityId[config.idField]) return true
        // For organizations, also match by org_uuid
        if (entityType === 'organization' && entityId.org_uuid && row.org_uuid === entityId.org_uuid) return true
        // For deals, also match by deal_uuid
        if (entityType === 'deal' && entityId.deal_uuid && row.deal_uuid === entityId.deal_uuid) return true
        return false
      }

      // 1) Update main SEARCH_DATA_KEY cache
      if (saveForRollback) {
        const prevData = queryClient.getQueryData(SEARCH_DATA_KEY)
        if (prevData) {
          rollbackRef.current.push({ key: SEARCH_DATA_KEY, data: prevData })
        }
      }

      queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
        if (!oldData?.[config.dataKey]) return oldData

        const updatedArray = oldData[config.dataKey].map((item: OrganizationTableItem) =>
          isMatch(item) ? { ...item, ...updates } : item
        )

        return {
          ...oldData,
          [config.dataKey]: updatedArray,
        }
      })

      // 2) Update server-side search queries
      queryClient.setQueriesData(
        {
          predicate: q =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'search' &&
            config.searchKeys.includes(q.queryKey[1] as string),
        },
        (oldData: any) => {
          if (!oldData?.rows) return oldData

          return {
            ...oldData,
            rows: oldData.rows.map((row: any) => (isMatch(row) ? { ...row, ...updates } : row)),
          }
        }
      )

      // 3) Update individual entity caches
      const idValue = entityId[config.idField] || entityId.PK
      if (idValue) {
        queryClient.setQueryData([entityType, idValue], (old: any) => (old ? { ...old, ...updates } : old))
      }

      // Update common cache key variations
      if (entityId.org_uuid) {
        queryClient.setQueryData(['organization', entityId.org_uuid], (old: any) =>
          old ? { ...old, ...updates } : old
        )
        queryClient.setQueryData(['organizationById', entityId.org_uuid], (old: any) =>
          old ? { ...old, ...updates } : old
        )
      }
      if (entityId.deal_uuid) {
        queryClient.setQueryData(['deal', entityId.deal_uuid], (old: any) =>
          old ? { ...old, ...updates } : old
        )
      }
      // Also update ['item', PK, SK] cache (used by some deal queries)
      if (entityId.PK && entityId.SK) {
        queryClient.setQueryData(['item', entityId.PK, entityId.SK], (old: any) =>
          old ? { ...old, ...updates } : old
        )
      }
    },
    [queryClient]
  )

  /**
   * Rollback all cached data to previous state
   */
  const rollback = useCallback(() => {
    rollbackRef.current.forEach(({ key, data }) => {
      queryClient.setQueryData(key, data)
    })
    rollbackRef.current = []
  }, [queryClient])

  /**
   * Update multiple fields on an entity (field-level API)
   */
  const updateEntity = useCallback(
    async (
      entityType: EntityType,
      entityId: EntityId,
      updates: Record<string, any>,
      opts?: { usersIndex?: Map<string, any>; extraQueryKeys?: QueryKey[]; silent?: boolean }
    ): Promise<void> => {
      const config = ENTITY_CONFIG[entityType]
      rollbackRef.current = []

      // Normalize all update values
      const normalizedUpdates: Record<string, any> = {}
      Object.entries(updates).forEach(([field, value]) => {
        normalizedUpdates[field] = normalizeValue(field, value, opts?.usersIndex)
      })

      // 1) Optimistic: Update grid immediately
      const rowId = getGridRowId(entityId, entityType)
      updateGridRow(rowId, normalizedUpdates)

      // 2) Optimistic: Update all caches
      updateCaches(entityType, entityId, normalizedUpdates, true)

      try {
        // 3) Persist to server
        const fieldUpdates = Object.entries(normalizedUpdates).map(([fieldName, newValue]) => ({
          tableName: config.tableName,
          PK: entityId.PK,
          SK: entityId.SK,
          fieldName,
          newValue,
        }))

        await updateFields({ updates: fieldUpdates })

        // 4) Update any extra query keys the caller specified
        opts?.extraQueryKeys?.forEach(key => {
          queryClient.setQueryData(key, (prev: any) =>
            prev ? { ...prev, ...normalizedUpdates } : prev
          )
        })

        if (showNotifications && !opts?.silent) {
          showSnackbar(
            `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} updated successfully`,
            'success'
          )
        }

        // Clear rollback data on success
        rollbackRef.current = []
      } catch (error) {
        // Rollback on error
        rollback()

        if (showNotifications && !opts?.silent) {
          showSnackbar(`Failed to update ${entityType}`, 'error')
        }

        throw error
      }
    },
    [updateGridRow, updateCaches, updateFields, queryClient, showNotifications, showSnackbar, rollback]
  )

  /**
   * Update a single field on an entity (convenience method)
   */
  const updateEntityField = useCallback(
    async (
      entityType: EntityType,
      entityId: EntityId,
      fieldName: string,
      newValue: any,
      opts?: { usersIndex?: Map<string, any>; extraQueryKeys?: QueryKey[]; silent?: boolean }
    ): Promise<void> => {
      return updateEntity(entityType, entityId, { [fieldName]: newValue }, opts)
    },
    [updateEntity]
  )

  // ============================================================================
  // Legacy mutation hooks (backward compatible with useUpdateOrganization, etc.)
  // ============================================================================

  /**
   * Legacy organization update mutation (replaces useUpdateOrganization)
   * Uses /aws/organizations/update/{orgId} endpoint
   */
  const organizationMutation = useMutation({
    mutationFn: async ({ orgId, orgData }: UpdateOrgVariables) => {
      const url = `${baseUrl}/aws/organizations/update/${orgId}`
      const { data } = await axios.put(url, orgData)
      return data
    },
    onMutate: async ({ orgId, orgData }) => {
      await queryClient.cancelQueries({ queryKey: ['organization', orgId] })
      const previousOrg = queryClient.getQueryData(['organization', orgId])

      // Optimistic update to individual cache
      queryClient.setQueryData(['organization', orgId], (old: any) =>
        old ? { ...old, ...orgData } : orgData
      )

      // Also update search caches
      updateCaches('organization', { org_uuid: orgId }, orgData as Record<string, any>, false)

      return { previousOrg }
    },
    onError: (_err, { orgId }, context) => {
      if (context?.previousOrg) {
        queryClient.setQueryData(['organization', orgId], context.previousOrg)
      }
    },
    onSuccess: (_data, { orgId, orgData }) => {
      // Ensure caches are in sync
      updateCaches('organization', { org_uuid: orgId }, orgData as Record<string, any>, false)
    },
  })

  /**
   * Legacy organization item update mutation (replaces useUpdateOrganizationItem)
   * Uses /aws/organizations_table/update-organization endpoint
   */
  const organizationItemMutation = useMutation({
    mutationFn: async ({ PK, SK, orgData }: UpdateOrgItemVariables) => {
      const url = `${baseUrl}/aws/organizations_table/update-organization`
      const { data } = await axios.put(url, { PK, SK, orgData })
      return data
    },
    onMutate: async ({ PK, SK, orgData }) => {
      const previousData = queryClient.getQueryData(SEARCH_DATA_KEY)

      // Optimistic update
      updateCaches('organization', { PK, SK }, orgData as Record<string, any>, false)

      return { previousData }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(SEARCH_DATA_KEY, context.previousData)
      }
    },
    onSuccess: (_data, { PK, SK, orgData }) => {
      // Ensure caches are in sync
      updateCaches('organization', { PK, SK }, orgData as Record<string, any>, false)
    },
  })

  /**
   * Legacy deal update mutation (replaces useUpdateDeal and useDealUpdate)
   * Uses /aws/organizations_table/updatedeal/{PK}/{SK} endpoint
   */
  const dealMutation = useMutation({
    mutationFn: async ({ PK, SK, dealData }: UpdateDealVariables) => {
      const url = `${baseUrl}/aws/organizations_table/updatedeal/${PK}/${SK}`
      const { data } = await axios.put(url, dealData)
      return data
    },
    onMutate: async ({ PK, SK, dealData }) => {
      await queryClient.cancelQueries({ queryKey: ['item', PK, SK] })
      const previousDeal = queryClient.getQueryData(['item', PK, SK])

      // Optimistic update to item cache
      queryClient.setQueryData(['item', PK, SK], (old: any) =>
        old ? { ...old, ...dealData } : dealData
      )

      // Also update search caches
      updateCaches('deal', { PK, SK }, dealData as Record<string, any>, false)

      return { previousDeal }
    },
    onError: (_err, { PK, SK }, context) => {
      if (context?.previousDeal) {
        queryClient.setQueryData(['item', PK, SK], context.previousDeal)
      }
    },
    onSuccess: (_data, { PK, SK, dealData }) => {
      // Ensure caches are in sync
      updateCaches('deal', { PK, SK }, dealData as Record<string, any>, false)
    },
  })

  return {
    // New centralized methods
    updateEntity,
    updateEntityField,
    updateCaches,

    // Legacy mutation objects (for backward compatibility)
    // These can be used like: updateOrganization.mutate({ orgId, orgData })
    updateOrganization: organizationMutation,
    updateOrganizationItem: organizationItemMutation,
    updateDeal: dealMutation,

    // Convenience mutation functions
    mutateOrganization: organizationMutation.mutate,
    mutateOrganizationAsync: organizationMutation.mutateAsync,
    mutateOrganizationItem: organizationItemMutation.mutate,
    mutateOrganizationItemAsync: organizationItemMutation.mutateAsync,
    mutateDeal: dealMutation.mutate,
    mutateDealAsync: dealMutation.mutateAsync,
  }
}

export default useEntityUpdate

// ============================================================================
// Backward-compatible hook exports
// These allow existing code to continue working with minimal changes
// ============================================================================

/**
 * @deprecated Use useEntityUpdate().updateOrganization instead
 */
export const useUpdateOrganization = () => {
  const { updateOrganization } = useEntityUpdate({ showNotifications: false })
  return updateOrganization
}

/**
 * @deprecated Use useEntityUpdate().updateOrganizationItem instead
 */
export const useUpdateOrganizationItem = () => {
  const { updateOrganizationItem } = useEntityUpdate({ showNotifications: false })
  return updateOrganizationItem
}

/**
 * @deprecated Use useEntityUpdate().updateDeal instead
 */
export const useUpdateDeal = () => {
  const { updateDeal } = useEntityUpdate({ showNotifications: false })
  return updateDeal
}

/**
 * @deprecated Use useEntityUpdate().updateDeal instead
 */
export const useDealUpdate = () => {
  const { updateDeal } = useEntityUpdate({ showNotifications: false })
  return updateDeal
}
